import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import type { NumericTextBoxChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { NumericTextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { memo, useCallback, useEffect, useState } from "react";

import type { EditDescriptor } from "~/features/commodities/hooks/use-commodity-editing";
import type { HandlingUnit } from "~/shared/types/quote-details-types";

type DimensionsCellProps = GridCustomCellProps & {
	edit: EditDescriptor;
};

// Extended type for searchable data items
type SearchableHandlingUnit = HandlingUnit & {
	originalDimensionLength?: number;
	originalDimensionWidth?: number;
	originalDimensionHeight?: number;
};

export const DimensionsCell = memo(
	({ edit, ...gridProps }: DimensionsCellProps) => {
		const dataItem = gridProps.dataItem as SearchableHandlingUnit | undefined;

		const isInEdit = dataItem ? edit[dataItem.id] === true : false;

		// Use original values if available (for searchable data), otherwise use current values
		const actualLength =
			dataItem?.originalDimensionLength ?? dataItem?.dimensionLength ?? 0;
		const actualWidth =
			dataItem?.originalDimensionWidth ?? dataItem?.dimensionWidth ?? 0;
		const actualHeight =
			dataItem?.originalDimensionHeight ?? dataItem?.dimensionHeight ?? 0;

		const [localLength, setLocalLength] = useState<number | null>(actualLength);
		const [localWidth, setLocalWidth] = useState<number | null>(actualWidth);
		const [localHeight, setLocalHeight] = useState<number | null>(actualHeight);

		useEffect(() => {
			if (dataItem) {
				setLocalLength(actualLength);
				setLocalWidth(actualWidth);
				setLocalHeight(actualHeight);
			}
		}, [dataItem, actualLength, actualWidth, actualHeight, isInEdit]);

		const commitChanges = useCallback(() => {
			if (gridProps.onChange && dataItem) {
				const changes = [];
				if (localLength !== actualLength) {
					changes.push({ field: "dimensionLength", value: localLength });
				}
				if (localWidth !== actualWidth) {
					changes.push({ field: "dimensionWidth", value: localWidth });
				}
				if (localHeight !== actualHeight) {
					changes.push({ field: "dimensionHeight", value: localHeight });
				}

				for (const { field, value } of changes) {
					gridProps.onChange?.({
						dataIndex: 0,
						dataItem: gridProps.dataItem as HandlingUnit,
						field,
						syntheticEvent: {} as React.SyntheticEvent,
						value,
					});
				}
			}
		}, [
			localLength,
			localWidth,
			localHeight,
			dataItem,
			gridProps,
			actualLength,
			actualWidth,
			actualHeight,
		]);

		const handleLengthChange = useCallback((e: NumericTextBoxChangeEvent) => {
			setLocalLength(e.target.value);
		}, []);

		const handleWidthChange = useCallback((e: NumericTextBoxChangeEvent) => {
			setLocalWidth(e.target.value);
		}, []);

		const handleHeightChange = useCallback((e: NumericTextBoxChangeEvent) => {
			setLocalHeight(e.target.value);
		}, []);

		const handleBlur = useCallback(() => {
			commitChanges();
		}, [commitChanges]);

		// Guard clause: if no dataItem, render empty cell (after all hooks)
		if (!dataItem) {
			return <td {...gridProps.tdProps} />;
		}

		const displayValue = `${actualLength} x ${actualWidth} x ${actualHeight}`;

		return (
			<td {...gridProps.tdProps}>
				{isInEdit ? (
					<div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
						<NumericTextBox
							onBlur={handleBlur}
							onChange={handleLengthChange}
							placeholder={"Length"}
							spinners={false}
							value={localLength}
						/>
						<span>x</span>
						<NumericTextBox
							onBlur={handleBlur}
							onChange={handleWidthChange}
							placeholder={"Width"}
							spinners={false}
							value={localWidth}
						/>
						<span>x</span>
						<NumericTextBox
							onBlur={handleBlur}
							onChange={handleHeightChange}
							placeholder={"Height"}
							spinners={false}
							value={localHeight}
						/>
					</div>
				) : (
					displayValue
				)}
			</td>
		);
	},
);

DimensionsCell.displayName = "DimensionsCell";
